/**
 * Theme store — one source of truth for light/dark.
 *
 * Three surfaces flip the theme: the sidebar toggle, the floating pill on the
 * unauthenticated routes, and the ⌘K palette's "Toggle theme" command. Before
 * this module each of them wrote `data-theme` and localStorage directly, so
 * flipping from the palette left the pill's own `useState` stale — the knob and
 * label kept showing the previous theme until something else remounted it.
 *
 * The DOM attribute stays the real state (the boot script in index.html sets it
 * before first paint, ahead of any React), and subscribers are notified on
 * every write so the two controls can never disagree.
 */

export type Theme = "dark" | "light";

const STORAGE_KEY = "cspro_theme";

const listeners = new Set<() => void>();

export function getTheme(): Theme {
  return document.documentElement.getAttribute("data-theme") === "light" ? "light" : "dark";
}

export function setTheme(next: Theme): void {
  document.documentElement.setAttribute("data-theme", next);
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* storage may be blocked; the in-memory toggle still works */
  }
  listeners.forEach((fn) => fn());
}

export function toggleTheme(): Theme {
  const next: Theme = getTheme() === "dark" ? "light" : "dark";
  setTheme(next);
  return next;
}

/** Subscribe shape for `useSyncExternalStore`. */
export function subscribeTheme(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

/**
 * Server snapshot for `useSyncExternalStore`. The app is client-rendered, so
 * this only guards the first pass; "dark" matches the pre-paint default.
 */
export function serverTheme(): Theme {
  return "dark";
}
